import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LogIn, Clock, MapPin, LogOut as LogOutIcon } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1593113598332-cd288d649433?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHx2b2x1bnRlZXJzJTIwY29tbXVuaXR5JTIwaGVscGluZ3xlbnwwfHx8fDE3ODgyMTAwNzF8MA&ixlib=rb-4.1.0&q=85";

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export default function TimeClock() {
  const now = useClock();
  const [events, setEvents] = useState([]);
  const [name, setName] = useState("");
  const [eventId, setEventId] = useState("");
  const [active, setActive] = useState(null); // active record if clocked in
  const [loading, setLoading] = useState(false);

  const loadEvents = async () => {
    try {
      const { data } = await api.get("/events/public");
      setEvents(data);
    } catch {
      toast.error("Could not load events.");
    }
  };

  useEffect(() => { loadEvents(); }, []);

  // Check active status when name & event selected
  useEffect(() => {
    const check = async () => {
      if (!name.trim() || !eventId) { setActive(null); return; }
      try {
        const { data } = await api.get("/timeclock/active", {
          params: { volunteer_name: name.trim(), event_id: eventId },
        });
        setActive(data);
      } catch { setActive(null); }
    };
    check();
  }, [name, eventId]);

  const selectedEvent = useMemo(
    () => events.find((e) => e.id === eventId), [events, eventId]
  );

  const handleClockIn = async () => {
    if (!name.trim()) { toast.error("Please enter your name."); return; }
    if (!eventId) { toast.error("Please select an event."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/timeclock/clock-in", {
        volunteer_name: name.trim(), event_id: eventId,
      });
      setActive(data);
      toast.success(`Clocked in — welcome, ${data.volunteer_name}!`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not clock in.");
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!active?.id) return;
    setLoading(true);
    try {
      const { data } = await api.post("/timeclock/clock-out", { record_id: active.id });
      const mins = data.duration_minutes ?? 0;
      const hrs = Math.floor(mins / 60);
      const rem = Math.round(mins - hrs * 60);
      toast.success(`Clocked out — thanks for volunteering (${hrs}h ${rem}m)!`);
      setActive(null);
      setName("");
      setEventId("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not clock out.");
    } finally {
      setLoading(false);
    }
  };

  const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dateStr = now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-brand-bg" data-testid="timeclock-page">
      {/* Left panel */}
      <div className="flex-1 flex flex-col justify-between p-8 sm:p-12 lg:p-16">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-brand-primary flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-lg font-semibold tracking-tight text-brand-text">Volunteer Clock</span>
          </div>
          <Link
            to="/login"
            data-testid="admin-login-link"
            className="text-sm text-brand-muted hover:text-brand-primary transition-colors font-body"
          >
            Admin sign in <LogIn className="inline w-4 h-4 ml-1" />
          </Link>
        </div>

        <div className="max-w-xl mx-auto w-full py-10 lg:py-0">
          <p className="overline mb-4" data-testid="current-date">{dateStr}</p>
          <h1
            className="font-heading text-6xl sm:text-7xl lg:text-8xl font-light tracking-tighter text-brand-text tabular-nums"
            data-testid="current-time"
          >
            {timeStr}
          </h1>
          <p className="mt-6 text-brand-muted font-body max-w-md leading-relaxed">
            Thank you for showing up. Enter your name, pick your event, and clock in when you begin.
          </p>

          <div className="mt-10 space-y-5">
            <div>
              <Label htmlFor="volunteer-name" className="overline block mb-2">Your name</Label>
              <Input
                id="volunteer-name"
                data-testid="volunteer-name-input"
                placeholder="e.g. Alex Rivera"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-14 text-lg bg-white border-brand-border font-body rounded-md"
                disabled={!!active}
              />
            </div>
            <div>
              <Label className="overline block mb-2">Event</Label>
              <Select value={eventId} onValueChange={setEventId} disabled={!!active}>
                <SelectTrigger
                  data-testid="event-select-trigger"
                  className="h-14 text-lg bg-white border-brand-border font-body rounded-md"
                >
                  <SelectValue placeholder="Select an event" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {events.length === 0 && (
                    <div className="px-3 py-2 text-sm text-brand-muted">No active events yet.</div>
                  )}
                  {events.map((ev) => (
                    <SelectItem key={ev.id} value={ev.id} data-testid={`event-option-${ev.id}`}>
                      <div className="flex flex-col text-left">
                        <span className="font-medium">{ev.name}</span>
                        {ev.location && (
                          <span className="text-xs text-brand-muted flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {ev.location}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {active ? (
              <div className="rounded-md border border-brand-border bg-white p-4 flex items-center justify-between" data-testid="active-session-banner">
                <div>
                  <p className="overline">Currently clocked in</p>
                  <p className="font-heading text-lg mt-1">
                    {active.volunteer_name} · {selectedEvent?.name || active.event_name}
                  </p>
                  <p className="text-sm text-brand-muted mt-1">
                    Since {new Date(active.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            ) : null}

            <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                data-testid="clock-in-button"
                onClick={handleClockIn}
                disabled={loading || !!active}
                className="h-16 rounded-full bg-brand-primary text-white font-heading text-lg font-medium transition-colors hover:bg-brand-primaryHover disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <LogIn className="w-5 h-5" /> Clock In
              </button>
              <button
                data-testid="clock-out-button"
                onClick={handleClockOut}
                disabled={loading || !active}
                className="h-16 rounded-full bg-brand-warm text-white font-heading text-lg font-medium transition-colors hover:bg-brand-warmHover disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <LogOutIcon className="w-5 h-5" /> Clock Out
              </button>
            </div>
          </div>
        </div>

        <p className="text-xs text-brand-muted font-body">
          © {new Date().getFullYear()} Volunteer Time Clock
        </p>
      </div>

      {/* Right image */}
      <div className="hidden lg:block flex-1 relative">
        <img src={IMG} alt="Volunteers helping community" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/10" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="overline text-white/80">Community · Growth · Service</p>
          <h2 className="font-heading text-4xl font-light tracking-tight mt-3 max-w-md">
            Every hour you give shapes the neighborhood we share.
          </h2>
        </div>
      </div>
    </div>
  );
}
